// src/modules/clinical-notes-v2/components/CareGapSuggestionCard.jsx
//
// Card de un care gap con status chip + checkbox para seleccionar.
// Si AI assistance está activa (prop `aiEnabled`), muestra un botón
// "Plan of action" que dispara la sugerencia IA. Cuando ya está lista,
// el card se expande inline para mostrar el plan completo (HPI, Exam,
// Assessment, Plan, Follow-up, Doc cues, ICD/CPT codes) y un botón
// "Apply to draft" que appendea las partes relevantes al editor.

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";

import AutoAwesomeIcon  from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon   from "@mui/icons-material/ExpandMore";
import ExpandLessIcon   from "@mui/icons-material/ExpandLess";
import NorthIcon        from "@mui/icons-material/North";

const STATUS_COLORS = {
  open:      { bg: "#ffebee", border: "#e53935", chip: "error",   label: "OPEN" },
  submitted: { bg: "#fff8e1", border: "#fb8c00", chip: "warning", label: "SUBMITTED" },
  closed:    { bg: "#e8f5e9", border: "#43a047", chip: "success", label: "CLOSED" },
};

const resolveStatusKey = (gap) => {
  const k = (gap?._sourceStatusCode || gap?.sourceStatusCode || gap?.status || "OPEN")
    .toString().toUpperCase();
  if (k === "SUBMITTED") return "submitted";
  if (k === "CLOSED")    return "closed";
  return "open";
};

export default function CareGapSuggestionCard({
  gap,
  selected,
  onToggleSelected,
  suggestionState,
  onRequestSuggestion,
  onApplyPlanToDraft, // (gap, suggestionData) => void
  aiEnabled = true,
  disabled = false,
}) {
  const status     = suggestionState?.status || "idle";
  const isReady    = status === "ready";
  const statusKey  = resolveStatusKey(gap);
  const palette    = STATUS_COLORS[statusKey];

  const [expanded, setExpanded] = React.useState(false);
  // Auto-expand when suggestion becomes ready
  React.useEffect(() => { if (isReady) setExpanded(true); }, [isReady]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderLeft: `5px solid ${palette.border}`,
        borderRadius: 2,
        transition: "transform .2s, box-shadow .2s",
        bgcolor: selected ? palette.bg : "background.paper",
        "&:hover": { boxShadow: 3, transform: "translateY(-1px)" },
      }}
    >
      <CardContent sx={{ "&:last-child": { pb: 1.5 }, py: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Checkbox
            edge="start"
            checked={!!selected}
            onChange={(e) => onToggleSelected(gap.careGapId, e.target.checked)}
            disabled={disabled}
            size="small"
            sx={{ mt: -0.5 }}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
              <Chip size="small" color={palette.chip} label={palette.label} sx={{ fontWeight: 800, height: 20 }} />
              {isReady && (
                <Chip
                  size="small"
                  icon={<CheckCircleIcon />}
                  color="primary"
                  variant="outlined"
                  label="Plan ready"
                  sx={{ height: 20 }}
                />
              )}
            </Stack>

            <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.25 }} title={gap.measure}>
              {gap.measure || "—"}
            </Typography>

            <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: "wrap", rowGap: 0.5 }}>
              {gap.qm && <Chip size="small" variant="outlined" label={gap.qm} />}
              {gap.icd10 && <Chip size="small" variant="outlined" label={gap.icd10} />}
              {gap.category && <Chip size="small" variant="outlined" label={gap.category} />}
              {gap._workflowStatus && <Chip size="small" variant="outlined" color="info" label={gap._workflowStatus} />}
              {gap.openSince && (
                <Tooltip title="Care gap open since">
                  <Chip size="small" variant="outlined" label={`📅 ${shortDate(gap.openSince)}`} />
                </Tooltip>
              )}
            </Stack>
          </Box>

          {aiEnabled && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                variant={isReady ? "outlined" : "contained"}
                color="primary"
                startIcon={status === "loading" ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
                onClick={() => onRequestSuggestion(gap)}
                disabled={disabled || status === "loading"}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                {status === "loading" ? "Generating…" : isReady ? "Regenerate" : "Plan of action"}
              </Button>
              {isReady && (
                <IconButton size="small" onClick={() => setExpanded((v) => !v)} sx={{ color: "primary.main" }}>
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>

        {status === "error" && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
            {suggestionState?.error || "Could not generate suggestion."}
          </Typography>
        )}

        {/* Inline action plan view */}
        {aiEnabled && (
          <Collapse in={isReady && expanded} timeout="auto" unmountOnExit>
            <InlineActionPlan
              data={suggestionState?.data}
              onApply={() => onApplyPlanToDraft?.(gap, suggestionState?.data)}
            />
          </Collapse>
        )}
      </CardContent>
    </Card>
  );
}

function InlineActionPlan({ data, onApply }) {
  const sug = data?.suggestion;
  if (!sug) return null;

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: "#e8f4ff",
        border: "1px solid #90caf9",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <AutoAwesomeIcon fontSize="small" sx={{ color: "#1565c0" }} />
        <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#0d47a1", flex: 1 }}>
          Action plan
        </Typography>
        {onApply && (
          <Button
            size="small"
            variant="contained"
            startIcon={<NorthIcon />}
            onClick={onApply}
            sx={{ textTransform: "none" }}
          >
            Apply to draft
          </Button>
        )}
      </Stack>

      <PlanBlock label="HPI"              value={sug.hpi} />
      <PlanBlock label="Exam findings"    value={sug.examFindings} />
      <PlanBlock label="Assessment"       value={sug.assessment} />
      <PlanBlock label="Plan"             list={sug.plan} />
      <PlanBlock label="Follow-up"        value={sug.followUp} />
      <PlanBlock label="Documentation cues" list={sug.documentationCues} />

      {(sug.icd10Codes?.length || sug.cptCodes?.length) ? (
        <>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
            {(sug.icd10Codes || []).map((c) => (
              <Chip key={`icd-${c}`} size="small" variant="outlined" color="primary" label={`ICD-10 ${c}`} />
            ))}
            {(sug.cptCodes || []).map((c) => (
              <Chip key={`cpt-${c}`} size="small" variant="outlined" color="success" label={`CPT ${c}`} />
            ))}
          </Stack>
        </>
      ) : null}
    </Box>
  );
}

function PlanBlock({ label, value, list }) {
  const hasValue = !!value?.trim?.();
  const hasList  = Array.isArray(list) && list.length > 0;
  if (!hasValue && !hasList) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, color: "#0d47a1", display: "block", mb: 0.25 }}>
        {label}
      </Typography>
      {hasValue && (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{value}</Typography>
      )}
      {hasList && (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {list.map((it, i) => (
            <li key={i}><Typography variant="body2">{it}</Typography></li>
          ))}
        </Box>
      )}
    </Box>
  );
}

function shortDate(d) {
  if (!d) return "";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return String(d).slice(0, 10);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}
